import { IRect } from "../types/IRect";
import { PolygonUtils } from "./PolygonUtils";

/**
 * Geometry and limits of an area's simulation grid.
 *
 * `IArea.resolution` — the horizontal cell count — stays the stored truth, and
 * cell size is always derived from it rather than the other way round. That
 * keeps `.fins` files unchanged, and it means the grid divides the rectangle
 * exactly: `GridProcessor` steps by `extent / resolution`, so there is never a
 * partial cell at an edge. A *requested* cell size is therefore rounded to the
 * nearest whole number of cells, and the size actually achieved can differ from
 * the one asked for by up to half a cell. Always show the achieved value.
 *
 * Every consumer must derive the vertical resolution through this class. It was
 * previously reimplemented in six places, which is how the floor-to-zero case
 * below survived unnoticed.
 */
class AreaGridUtils {

  /** Fewest cells across, and the coarsest grid the UI offers. */
  static MIN_RESOLUTION = 10;

  /** Most cells across, however wide and shallow the rectangle is. */
  static MAX_RESOLUTION = 500;

  /**
   * Ceiling on horizontal × vertical cells.
   *
   * Cost is per cell: one DEM lookup during grid generation, and ~265 bytes in
   * the ORGANIX payload (measured across three reference exports of 837, 5,159
   * and 8,526 cells). 250,000 cells is therefore roughly a 66 MB upload, which
   * is what the resolution slider already permitted for a square area — so this
   * limit takes nothing away that used to work.
   *
   * MAX_RESOLUTION alone does not bound the total, because the vertical count
   * scales with the aspect ratio: a 100 m × 10 km rectangle at resolution 500
   * works out at 50,000 rows, or 25 million cells.
   */
  static MAX_CELLS = 250000;

  /**
   * Cell size (m) a new area aims for. Seeding a cell *count* instead made a new
   * area's physical scale depend on how large its rectangle happened to be —
   * 20 m cells on a 200 m rectangle, 500 m cells on a 5 km one, from the same
   * default.
   */
  static DEFAULT_CELL_SIZE = 25;

  /** Width of the rectangle in meters, along its southern edge. */
  static width = (rect: IRect) =>
    PolygonUtils.distance(
      { lng: rect.minLng, lat: rect.minLat },
      { lng: rect.maxLng, lat: rect.minLat });

  /** Height of the rectangle in meters, along its western edge. */
  static height = (rect: IRect) =>
    PolygonUtils.distance(
      { lng: rect.minLng, lat: rect.minLat },
      { lng: rect.minLng, lat: rect.maxLat });

  /** Height / width. Falls back to 1 for a degenerate rectangle. */
  static ratio = (rect: IRect) => {
    const width = this.width(rect);
    if (width <= 0) return 1;
    return Math.abs(this.height(rect) / width);
  }

  /**
   * Most cells across this particular rectangle may have: the absolute cap, or
   * whatever keeps the total under MAX_CELLS, whichever bites first. Since the
   * total is resolution² × ratio, the cell budget allows √(MAX_CELLS / ratio)
   * columns.
   */
  static maxResolution = (rect: IRect) => {
    const ratio = this.ratio(rect);
    const byCellBudget = ratio > 0 ? Math.floor(Math.sqrt(this.MAX_CELLS / ratio)) : this.MAX_RESOLUTION;
    return Math.max(1, Math.min(this.MAX_RESOLUTION, byCellBudget));
  }

  /**
   * Fewest cells across. Normally MIN_RESOLUTION, but a rectangle tall enough
   * for the cell budget to bite below that gets the lower figure, so the range
   * never inverts.
   */
  static minResolution = (rect: IRect) =>
    Math.min(this.MIN_RESOLUTION, this.maxResolution(rect));

  /**
   * Vertical cell count for a given horizontal one, keeping cells as square as
   * an integer count allows. Never zero: a wide, shallow rectangle (say
   * 1000 m × 20 m at resolution 10) floors to nothing, which left GridProcessor
   * with an infinite latitude step and sent `matrixHeight: 0` to ORGANIX.
   */
  static verticalResolution = (rect: IRect, resolution: number) =>
    Math.max(1, Math.floor(resolution * this.ratio(rect)));

  /** Total cells in the grid. */
  static cellCount = (rect: IRect, resolution: number) =>
    resolution * this.verticalResolution(rect, resolution);

  /** Width of one cell in meters. */
  static cellWidth = (rect: IRect, resolution: number) =>
    this.width(rect) / resolution;

  /**
   * Height of one cell in meters. Slightly more than the width, because
   * flooring the vertical count spreads the height over fewer rows.
   */
  static cellHeight = (rect: IRect, resolution: number) =>
    this.height(rect) / this.verticalResolution(rect, resolution);

  /** Smallest cell this rectangle allows, i.e. the one at maxResolution. */
  static smallestCellSize = (rect: IRect) =>
    this.width(rect) / this.maxResolution(rect);

  /** Largest cell this rectangle allows, i.e. the one at minResolution. */
  static largestCellSize = (rect: IRect) =>
    this.width(rect) / this.minResolution(rect);

  /**
   * Horizontal resolution that comes closest to a requested cell size, clamped
   * to what this rectangle allows. Returns the clamped resolution and whether
   * clamping happened, so the UI can say why it didn't get what it asked for.
   */
  static resolutionForCellSize = (rect: IRect, cellSize: number): { resolution: number, clamped: boolean } => {
    const min = this.minResolution(rect);
    const max = this.maxResolution(rect);
    if (!(cellSize > 0)) return { resolution: max, clamped: true };
    const requested = Math.round(this.width(rect) / cellSize);
    const resolution = Math.min(max, Math.max(min, requested));
    return { resolution, clamped: resolution !== requested };
  }
}

export { AreaGridUtils }
